import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  LiveRatesPe,
  PeruRail,
  PERU_RAILS,
  PeruService,
  ClpPenConfig,
  ClpPenLiveRates,
  ClpPenPreview,
  ClpPenPreviewItem,
  ClpPenTasaSource,
} from '../../services/peru.service';

/** Form de la pata CLP→USD de Perú (singleton pais='PE'; la pata USD→PEN se configura aparte). */
interface ClpForm {
  activo: boolean;
  tasaSource: ClpPenTasaSource;
  tasaManual: number | null;
  tasaManualVigenteHasta: string | null;
  markupPct: number | null;
  minClp: number | null;
  motivo: string;
}

const DEFAULT_FORM: ClpForm = {
  activo: false,
  tasaSource: 'LIVE_SELL',
  tasaManual: null,
  tasaManualVigenteHasta: null,
  markupPct: 0,
  minClp: 0,
  motivo: '',
};

/** Mínimo del rail (USD), igual que USD→PEN. */
const MIN_USD = 20;
/** Montos CLP del preview (≈ 20 / 65 / 130 / 1300 USD a ~1000 CLP/USD). */
const PREVIEW_MONTOS_CLP = [20000, 65000, 130000, 1300000];

export interface RailClpPenConfigData {
  countryCode?: string; // 'PE'
  countryName?: string;
}

/**
 * MODAL "CLP → PEN — Configuración" (pata CLP→USD). Config PROPIA e
 * INDEPENDIENTE de CLP→COP (`clp_cop_config` con `pais='PE'`). El usuario chileno
 * paga con su saldo CLP y el beneficiario en PE recibe PEN — ve UNA sola tasa
 * PEN/CLP. Acá se gobierna solo la conversión CLP→USD (fuente LIVE_SELL/MANUAL +
 * markup + activación); la pata USD→PEN (Vita, ESPEJO) se configura en "USD/PEN".
 * Espejo de `RailClpCopConfigComponent` + `RailVesPenConfigComponent`.
 */
@Component({
  selector: 'app-rail-clp-pen-config',
  templateUrl: './rail-clp-pen-config.component.html',
  styleUrls: ['./rail-clp-pen-config.component.sass'],
})
export class RailClpPenConfigComponent implements OnInit, OnDestroy {
  readonly rails: PeruRail[] = PERU_RAILS;
  readonly railLabel: Record<PeruRail, string> = {
    PE: 'Interbancario (CCI · instantáneo)',
  };
  readonly sources: { value: ClpPenTasaSource; label: string; hint: string }[] = [
    { value: 'LIVE_SELL', label: 'Tasa de venta (vivo)', hint: 'valores_cambio_sell (la que ve el cliente en CLP→USDT)' },
    { value: 'MANUAL', label: 'Manual (override)', hint: 'Tasa fija con vencimiento' },
  ];

  config: ClpPenConfig | null = null;
  live: ClpPenLiveRates | null = null;
  /** Tasas vivas de la pata USD→PEN (Vita) — para mostrar la tasa Vita actual. */
  usdLive: LiveRatesPe | null = null;

  form: ClpForm = { ...DEFAULT_FORM };

  preview: ClpPenPreview | null = null;
  previewLoading = false;
  private previewTimer: any = null;
  private previewSeq = 0;

  /** USD→PEN (master). Si OFF, no se puede ofrecer CLP→PEN (va por ese puente). */
  usdtPenOfrecido = true;
  loading = false;
  saving = false;
  error: string | null = null;
  okMsg: string | null = null;

  constructor(
    private svc: PeruService,
    public dialogRef: MatDialogRef<RailClpPenConfigComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RailClpPenConfigData,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    if (this.previewTimer) clearTimeout(this.previewTimer);
  }

  async load(): Promise<void> {
    this.loading = true;
    this.error = null;
    try {
      const [config, live, usdLive, railes] = await Promise.all([
        this.svc.getClpConfig(),
        this.svc.getClpLive(),
        this.svc.getLive().catch(() => null),
        this.svc.getRailesActivos().catch(() => null),
      ]);
      this.config = config;
      this.live = live;
      this.usdLive = usdLive;
      this.fromConfig(config);
      this.form.activo = !!railes?.pen?.clp_pen;
      this.usdtPenOfrecido = railes?.pen?.usdt_pen !== false;
      if (!this.usdtPenOfrecido) this.form.activo = false;
      this.refreshPreview(true);
    } catch (e: any) {
      this.error = e?.error?.message || e?.message || 'No se pudo cargar la configuración';
    } finally {
      this.loading = false;
    }
  }

  private fromConfig(c: ClpPenConfig | null): void {
    if (!c) {
      this.form = { ...DEFAULT_FORM };
      return;
    }
    this.form = {
      activo: false, // lo setea load() desde el switchboard
      tasaSource: c.tasaSource === 'MANUAL' ? 'MANUAL' : 'LIVE_SELL',
      tasaManual: c.tasaManual != null ? Number(c.tasaManual) : null,
      tasaManualVigenteHasta: c.tasaManualVigenteHasta
        ? c.tasaManualVigenteHasta.slice(0, 16)
        : null,
      markupPct: c.markupPct != null ? Number(c.markupPct) : 0,
      minClp: c.minClp != null ? Number(c.minClp) : 0,
      motivo: '',
    };
  }

  onFormChange(): void {
    this.okMsg = null;
    this.refreshPreview();
  }

  /** Toggle "Ofrecer CLP→PEN". Bloqueado si USD→PEN (master) está OFF. */
  toggleActivo(): void {
    if (!this.usdtPenOfrecido) return;
    this.form.activo = !this.form.activo;
    this.onFormChange();
  }

  refreshPreview(inmediato = false): void {
    if (this.previewTimer) clearTimeout(this.previewTimer);
    this.previewTimer = setTimeout(() => this.fetchPreview(), inmediato ? 0 : 600);
  }

  private async fetchPreview(): Promise<void> {
    const seq = ++this.previewSeq;
    this.previewLoading = true;
    try {
      const p = await this.svc.clpQuotePreview(PREVIEW_MONTOS_CLP, {
        tasaSource: this.form.tasaSource,
        tasaManual:
          this.form.tasaSource === 'MANUAL' && this.form.tasaManual != null
            ? Number(this.form.tasaManual)
            : undefined,
        tasaManualVigenteHasta:
          this.form.tasaSource === 'MANUAL' && this.form.tasaManualVigenteHasta
            ? new Date(this.form.tasaManualVigenteHasta).toISOString()
            : undefined,
        markupPct: this.form.markupPct != null ? Number(this.form.markupPct) : undefined,
        minClp: this.form.minClp != null ? Number(this.form.minClp) : undefined,
      });
      if (seq === this.previewSeq) this.preview = p;
    } catch {
      if (seq === this.previewSeq) this.preview = null;
    } finally {
      if (seq === this.previewSeq) this.previewLoading = false;
    }
  }

  async save(): Promise<void> {
    if (this.form.tasaSource === 'MANUAL') {
      if (!(Number(this.form.tasaManual) > 0) || !this.form.tasaManualVigenteHasta) {
        this.error = 'MANUAL requiere tasa y vencimiento futuros.';
        return;
      }
    }
    this.saving = true;
    this.error = null;
    this.okMsg = null;
    try {
      // 1) On/off del rail (switchboard; merge preserva usdt_pen/ves_pen).
      await this.svc.setRailesActivos({ clpPen: !!this.form.activo });
      // 2) Config de PRECIO (entity versionada pais='PE') — sin el on/off.
      await this.svc.upsertClpConfig({
        tasaSource: this.form.tasaSource,
        tasaManual:
          this.form.tasaSource === 'MANUAL' ? Number(this.form.tasaManual) : undefined,
        tasaManualVigenteHasta:
          this.form.tasaSource === 'MANUAL' && this.form.tasaManualVigenteHasta
            ? new Date(this.form.tasaManualVigenteHasta).toISOString()
            : undefined,
        markupPct: this.form.markupPct != null ? Number(this.form.markupPct) : undefined,
        minClp: this.form.minClp != null ? Number(this.form.minClp) : undefined,
        motivo: this.form.motivo || undefined,
      });
      this.okMsg = `Configuración guardada (${this.form.activo ? 'CLP→PEN activo' : 'CLP→PEN inactivo'}).`;
      await this.load();
    } catch (e: any) {
      this.error = e?.error?.message || e?.message || 'No se pudo guardar';
      await this.load();
    } finally {
      this.saving = false;
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  // ── Helpers de vista ──

  itemsDe(rail: PeruRail): ClpPenPreviewItem[] {
    return this.preview?.rails?.[rail] ?? [];
  }

  /** Tasa CLP/USD que se está usando (de la fuente activa + markup). */
  get tasaClpUsd(): number | null {
    return this.preview?.rate?.efectiva ?? this.live?.rate?.efectiva ?? null;
  }
  get tasaStale(): boolean {
    return !!(this.preview?.rate?.stale ?? this.live?.rate?.stale);
  }
  get sourceResuelta(): ClpPenTasaSource | null {
    return this.preview?.rate?.source ?? this.live?.rate?.source ?? null;
  }

  /** Mínimo (20 USD) expresado en CLP a la tasa actual. */
  get minClpEquivalente(): number | null {
    const r = this.tasaClpUsd;
    return r && r > 0 ? Math.ceil(MIN_USD * r) : null;
  }

  // ── Tasa Vita actual (pata de salida USD→PEN) ──
  vitaRetailPen(rail: PeruRail): number | null {
    return this.usdLive?.rails?.[rail]?.publicSellUsd ?? this.usdLive?.rails?.[rail]?.publicUsdSell ?? null;
  }
  vitaPartnerPen(rail: PeruRail): number | null {
    return this.usdLive?.rails?.[rail]?.partnerUsdSell ?? null;
  }

  private allItems(): ClpPenPreviewItem[] {
    return this.rails.flatMap((r) => this.itemsDe(r));
  }

  // ── Alertas (espejo de las del modal CLP→COP) ──

  get alertSinMargen(): boolean {
    return this.allItems().some((i) => i.cumpleMinimo && i.disponible && !i.margenOk);
  }
  get alertBajoVita(): boolean {
    return this.allItems().some(
      (i) => i.clamped || (i.ofertaVitaPen != null && i.recibePen < i.ofertaVitaPen),
    );
  }
  get alertEspejoDegradado(): boolean {
    return this.allItems().some((i) => i.espejoFallback);
  }
  get alertFeedStale(): boolean {
    return this.tasaStale || this.allItems().some((i) => i.stale);
  }
}
